import { useEffect, useState, useRef } from "react";
import {secondsToDuration} from "@/utils/textFormatting";

type TimeRangePickerProps = {
    minValue?: number;
    maxValue: number; // Video duration in seconds
    startValue?: number;
    endValue?: number;
    onChange: (values: { start: number; end: number }) => void;
    className?: string;
};

const TimeRangePicker = ({
     minValue = 0,
     maxValue,
     startValue = 0,
     endValue = 0,
     onChange,
     className = ""
 }: TimeRangePickerProps) => {
    const [start, setStart] = useState<number>(startValue);
    const [end, setEnd] = useState<number>(endValue);
    const [isDraggingStart, setIsDraggingStart] = useState(false);
    const [isDraggingEnd, setIsDraggingEnd] = useState(false);
    const [startInputValue, setStartInputValue] = useState<string>(secondsToDuration(startValue));
    const [endInputValue, setEndInputValue] = useState<string>(secondsToDuration(endValue));
    const sliderRef = useRef<HTMLDivElement>(null);
    const startInputRef = useRef<HTMLInputElement>(null);
    const endInputRef = useRef<HTMLInputElement>(null);
    const dummyStartRef = useRef<HTMLSpanElement>(null);
    const dummyEndRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        setStart(startValue);
        setEnd(endValue > 0 ? endValue : maxValue);
        setStartInputValue(secondsToDuration(startValue));
        setEndInputValue(secondsToDuration(endValue > 0 ? endValue : maxValue));
    }, [startValue, endValue, maxValue]);

    // Auto-resize inputs based on content
    useEffect(() => {
        const adjustInputSize = () => {
            if (startInputRef.current && dummyStartRef.current) {
                dummyStartRef.current.textContent = startInputValue;
                const width = dummyStartRef.current.offsetWidth;
                startInputRef.current.style.width = `${width + 16}px`; // Add padding
            }

            if (endInputRef.current && dummyEndRef.current) {
                dummyEndRef.current.textContent = endInputValue;
                const width = dummyEndRef.current.offsetWidth;
                endInputRef.current.style.width = `${width + 16}px`; // Add padding
            }
        };

        adjustInputSize();
    }, [startInputValue, endInputValue]);

    const calculatePosition = (value: number): number => {
        return ((value - minValue) / (maxValue - minValue)) * 100;
    };

    const calculateValue = (position: number): number => {
        if (!sliderRef.current) return minValue;
        const rect = sliderRef.current.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(1, (position - rect.left) / rect.width));
        return Math.round(minValue + percentage * (maxValue - minValue));
    };

    const handleMouseDown = (e: React.MouseEvent, isStart: boolean) => {
        if (isStart) {
            setIsDraggingStart(true);
        } else {
            setIsDraggingEnd(true);
        }
        e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDraggingStart && !isDraggingEnd) return;

        const newValue = calculateValue(e.clientX);

        if (isDraggingStart) {
            const boundedValue = Math.max(minValue, Math.min(end !== undefined ? end - 1 : maxValue, newValue));
            setStart(boundedValue);
            setStartInputValue(secondsToDuration(boundedValue));
            onChange({ start: boundedValue, end });
        } else if (isDraggingEnd) {
            const boundedValue = Math.max(start + 1, Math.min(maxValue, newValue));
            setEnd(boundedValue);
            setEndInputValue(secondsToDuration(boundedValue));
            onChange({ start, end: boundedValue });
        }
    };

    const handleMouseUp = () => {
        setIsDraggingStart(false);
        setIsDraggingEnd(false);
    };

    // Parse a time string in format mm:ss or hh:mm:ss to seconds
    const durationToSeconds = (durationStr: string): number | null => {
        // Match patterns like "mm:ss" or "hh:mm:ss"
        const pattern = /^(?:(\d+):)?(\d{1,2}):(\d{1,2})$/;
        const match = durationStr.match(pattern);

        if (!match) return null;

        const hours = match[1] ? parseInt(match[1], 10) : 0;
        const minutes = parseInt(match[2], 10);
        const seconds = parseInt(match[3], 10);

        // Basic validation
        if (minutes >= 60 || seconds >= 60) return null;

        return hours * 3600 + minutes * 60 + seconds;
    };

    const handleStartInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setStartInputValue(newValue);

        // Try to validate and update immediately if valid
        const seconds = durationToSeconds(newValue);
        if (seconds !== null && seconds >= minValue && seconds < (end ?? maxValue)) {
            // Valid input - update immediately
            setStart(seconds);
            onChange({ start: seconds, end });
        }
    };

    const handleEndInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setEndInputValue(newValue);

        // Try to validate and update immediately if valid
        const seconds = durationToSeconds(newValue);
        if (seconds !== null && seconds > start && seconds <= maxValue) {
            // Valid input - update immediately
            setEnd(seconds);
            onChange({ start, end: seconds });
        }
    };

    const validateAndUpdateStart = () => {
        const seconds = durationToSeconds(startInputValue);
        if (seconds !== null && seconds >= minValue && seconds < (end ?? maxValue)) {
            // Valid input
            setStart(seconds);
            onChange({ start: seconds, end });
        } else {
            // Invalid input, revert to current value
            setStartInputValue(secondsToDuration(start));
        }
    };

    const validateAndUpdateEnd = () => {
        const seconds = durationToSeconds(endInputValue);
        if (seconds !== null && seconds > start && seconds <= maxValue) {
            // Valid input
            setEnd(seconds);
            onChange({ start, end: seconds });
        } else {
            // Invalid input, revert to current value
            setEndInputValue(secondsToDuration(end !== undefined ? end : maxValue));
        }
    };

    useEffect(() => {
        if (isDraggingStart || isDraggingEnd) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDraggingStart, isDraggingEnd, start, end]);

    const startPos = calculatePosition(start);
    const endPos = calculatePosition(end !== undefined ? end : maxValue);

    return (
        <div className={`w-full ${className}`}>
            <div className="mb-3">
                <div
                    ref={sliderRef}
                    className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer"
                    onClick={(e) => {
                        const newValue = calculateValue(e.clientX);
                        const distToStart = Math.abs(newValue - start);
                        const distToEnd = Math.abs(newValue - (end !== undefined ? end : maxValue));

                        if (distToStart < distToEnd) {
                            const boundedValue = Math.max(minValue, Math.min(end !== undefined ? end - 1 : maxValue, newValue));
                            setStart(boundedValue);
                            setStartInputValue(secondsToDuration(boundedValue));
                            onChange({ start: boundedValue, end });
                        } else {
                            const boundedValue = Math.max(start + 1, Math.min(maxValue, newValue));
                            setEnd(boundedValue);
                            setEndInputValue(secondsToDuration(boundedValue));
                            onChange({ start, end: boundedValue });
                        }
                    }}
                >
                    <div
                        className="absolute h-full bg-blue-500 rounded-full"
                        style={{
                            left: `${startPos}%`,
                            width: `${endPos - startPos}%`
                        }}
                    ></div>

                    <div
                        className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-200 border-2 border-blue-500 dark:border-blue-400 rounded-full cursor-grab shadow-md hover:shadow-lg transition-shadow duration-200"
                        style={{ left: `${startPos}%` }}
                        onMouseDown={(e) => handleMouseDown(e, true)}
                    ></div>

                    <div
                        className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-200 border-2 border-blue-500 dark:border-blue-400 rounded-full cursor-grab shadow-md hover:shadow-lg transition-shadow duration-200"
                        style={{ left: `${endPos}%` }}
                        onMouseDown={(e) => handleMouseDown(e, false)}
                    ></div>
                </div>
            </div>

            <div className="flex flex-row justify-between items-center">
                <span
                    ref={dummyStartRef}
                    className="absolute invisible text-xs font-medium"
                    aria-hidden="true"
                ></span>
                <span
                    ref={dummyEndRef}
                    className="absolute invisible text-xs font-medium"
                    aria-hidden="true"
                ></span>

                <input
                    ref={startInputRef}
                    type="text"
                    value={startInputValue}
                    onChange={handleStartInputChange}
                    onBlur={validateAndUpdateStart}
                    onKeyDown={(e) => e.key === 'Enter' && validateAndUpdateStart()}
                    className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-gray-500/10 dark:ring-gray-600/20 ring-inset text-center"
                    style={{ border: 'none', minWidth: '40px' }}
                />
                <input
                    ref={endInputRef}
                    type="text"
                    value={endInputValue}
                    onChange={handleEndInputChange}
                    onBlur={validateAndUpdateEnd}
                    onKeyDown={(e) => e.key === 'Enter' && validateAndUpdateEnd()}
                    className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 ring-1 ring-gray-500/10 dark:ring-gray-600/20 ring-inset text-center"
                    style={{ border: 'none', minWidth: '40px' }}
                />
            </div>
        </div>
    );
};

export default TimeRangePicker;
